import React, { useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Check, Loader2, ArrowRight, Unlock, Crown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

/**
 * Upgrade gate (formerly NoCreditsPopup) — shown when a user hits the limit
 * of their monthly AI analyses.
 *
 * Spec (Feb 2026 pricing UX): no credit packages, no per-action pricing.
 * Two paths forward:
 *   1. Upgrade to the recommended plan (primary)
 *   2. Unlock just this feature for €1.99 / 7 days (bridge option)
 */
export default function NoCreditsPopup({ open, onClose, actionName }) {
  const { API, refreshUser, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";
  const [unlocking, setUnlocking] = useState(false);
  const [continuingFree, setContinuingFree] = useState(false);

  const subStatus = user?.subscription?.status;
  const isTrial = subStatus === "trialing";
  const isTrialExpired = subStatus === "trial_expired";

  const handleMicroUnlock = () => {
    // iter690: Stripe премахнат — Wise checkout на Pricing
    onClose?.();
    navigate("/pricing?intent=micro_unlock");
  };

  const handleContinueFree = async () => {
    setContinuingFree(true);
    try {
      await axios.post(
        `${API}/subscription/continue-free`,
        {},
        { headers: { Authorization: `Bearer ${(localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token"))}` } }
      );
      if (refreshUser) await refreshUser();
      toast.success(bg ? "Продължавате с безплатен план" : "Switched to Free plan");
      onClose?.();
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Error"));
    } finally {
      setContinuingFree(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={`sm:max-w-[460px] p-0 overflow-hidden border-0 ${isDark ? "bg-[hsl(222,47%,11%)]" : "bg-white"}`}
        data-testid="upgrade-gate-popup"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] p-5 text-center text-white">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold" data-testid="upgrade-gate-title">
            {isTrial
              ? (bg ? "Достигнахте лимита на пробния период" : "You've reached your trial limit")
              : isTrialExpired
                ? (bg ? "Пробният ви период приключи" : "Your trial has ended")
                : (bg ? "Надградете за по-дълбок анализ" : "Upgrade to unlock deeper analysis")}
          </h3>
          <p className="text-sm opacity-90 mt-1">
            {isTrial || isTrialExpired
              ? (bg
                  ? "Видяхте как Nutramilo анализира метаболизма ви. Изберете как да продължите."
                  : "You've seen how Nutramilo analyzes your metabolism. Choose how to continue.")
              : (bg
                  ? (actionName ? `${actionName} изисква активен план` : "Достигнахте лимита си за месеца")
                  : (actionName ? `${actionName} requires an active plan` : "You've reached this month's capacity"))}
          </p>
        </div>

        {/* Primary upgrade CTA */}
        <div className="p-4 space-y-3">
          <div
            className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all ${
              isDark ? "border-[#6366F1] bg-[#6366F1]/10" : "border-[#6366F1] bg-indigo-50/40"
            }`}
            onClick={() => {
              onClose?.();
              navigate("/pricing");
            }}
            data-testid="upgrade-gate-plan-cta"
          >
            <div className="absolute -top-2.5 left-4">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#6366F1] text-white shadow-sm">
                {bg ? "ПРЕПОРЪЧАНО" : "RECOMMENDED"}
              </span>
            </div>
            <div className="flex items-start gap-3 mt-1">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#0EA5E9] to-[#6366F1]">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? "Надградете плана си" : "Upgrade your plan"}
                </p>
                <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg
                    ? "Структурирани анализи, клинични изводи, адаптивен AI."
                    : "Structured analyses, clinical insights, adaptive AI."}
                </p>
                <div className={`mt-2 flex items-center gap-3 text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-500" />CORE €29</span>
                  <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-500" />PRO €59</span>
                </div>
              </div>
              <ArrowRight className={`w-4 h-4 mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
            </div>
          </div>

          {/* Bridge option — €1.99 micro-unlock */}
          <button
            onClick={handleMicroUnlock}
            disabled={unlocking}
            className={`w-full rounded-xl border p-3.5 transition-all text-left ${
              isDark
                ? "border-[hsl(223,47%,20%)] hover:border-[hsl(223,47%,30%)] bg-[hsl(223,47%,8%)]"
                : "border-gray-200 hover:border-gray-300 bg-white"
            }`}
            data-testid="upgrade-gate-micro-unlock"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-amber-500/15" : "bg-amber-50"}`}>
                <Unlock className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? "Отключи само този анализ" : "Unlock just this analysis"}
                </p>
                <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {bg ? "Пълен достъп за 7 дни — без абонамент" : "Full access for 7 days — no subscription"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-amber-500">€1.99</p>
                {unlocking && <Loader2 className="w-3 h-3 animate-spin mt-0.5 ml-auto text-amber-500" />}
              </div>
            </div>
          </button>

          {/* Continue with Free — only for trial / trial-expired users */}
          {(isTrial || isTrialExpired) && (
            <button
              onClick={handleContinueFree}
              disabled={continuingFree}
              className={`w-full rounded-xl border p-3 transition-all text-left ${
                isDark
                  ? "border-[hsl(223,47%,20%)] hover:border-[hsl(223,47%,30%)] bg-[hsl(223,47%,8%)]"
                  : "border-gray-200 hover:border-gray-300 bg-white"
              }`}
              data-testid="upgrade-gate-continue-free"
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-slate-700/60" : "bg-slate-100"}`}>
                  <Check className={`w-4 h-4 ${isDark ? "text-slate-300" : "text-slate-500"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                    {bg ? "Продължете с безплатния план" : "Continue with Free"}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {bg ? "Хранителен дневник, DIL, калкулатори" : "Food diary, DIL tracking, calculators"}
                  </p>
                </div>
                {continuingFree && <Loader2 className={`w-3 h-3 animate-spin ${isDark ? "text-gray-400" : "text-gray-500"}`} />}
              </div>
            </button>
          )}

          {/* Quiet dismiss */}
          <div className="text-center pt-1">
            <button
              onClick={onClose}
              className={`text-[11px] underline-offset-2 hover:underline ${isDark ? "text-gray-500" : "text-gray-400"}`}
              data-testid="upgrade-gate-dismiss"
            >
              {bg ? "Не сега" : "Not now"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
